import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BiUserCircle, BiPencil } from 'react-icons/bi';
import { API_BASE_URL } from '../utils/urls.js';

const UserProfile = () => {
    const { id } = useParams();
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const currentUser = JSON.parse(localStorage.getItem('user'));

    // Safer URL constructor to handle full vs relative paths
    const getLogoUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        const baseUrl = API_BASE_URL.split('/api')[0];
        return `${baseUrl}${path}`;
    };

    useEffect(() => {
        const fetchUserProfile = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_BASE_URL}/api/users/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setUserProfile(response.data);
            } catch (err) {
                setError('Failed to fetch user profile.');
                console.error('Error fetching user profile:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchUserProfile();
    }, [id]);

    if (loading) return <div className="text-center text-gray-500">Loading profile...</div>;
    if (error) return <div className="text-center text-red-500">{error}</div>;
    if (!userProfile) return <div className="text-center text-gray-500">No user data found.</div>;

    return (
        <div className="container mx-auto p-4 max-w-2xl">
            <div className="bg-white shadow-lg rounded-lg p-6">

                {/* Organization Logo Display */}
                {currentUser?.organization?.logoUrl && (
                    <div className="absolute top-6 right-6">
                        <img 
                            src={getLogoUrl(currentUser.organization.logoUrl)} 
                            alt={`${currentUser.organization.name} Logo`} 
                            className="h-12 sm:h-14 object-contain border rounded p-1 shadow-sm bg-white" 
                        />
                    </div>
                )}
                <div className="flex items-center space-x-4 mb-6">
                    <BiUserCircle className="w-16 h-16 text-blue-500" />
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">{userProfile.first_name} {userProfile.last_name}</h1>
                        <p className="text-gray-600">{userProfile.role_name}</p>
                    </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                    <dl className="divide-y divide-gray-200">
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Full name</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">{userProfile.first_name} {userProfile.last_name}</dd>
                        </div>
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Email address</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">{userProfile.email}</dd>
                        </div>
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Phone number</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">{userProfile.phone_number || 'N/A'}</dd>
                        </div>
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">Role</dt>
                            <dd className="mt-1 text-sm text-gray-900 sm:col-span-2">{userProfile.role_name}</dd>
                        </div>
                    </dl>
                </div>

                {/* Edit button only visible if user can edit this profile */}
                {(currentUser.roleName === 'admin' || currentUser.id === parseInt(id)) && (
                    <div className="mt-6 flex justify-end">
                        <Link to={`/users/${id}/edit`} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 transition">
                            <BiPencil className="mr-2" /> Edit Profile
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserProfile;